import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  Stack,
  Text,
  chakra,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';

import Checkbox from 'components/Checkbox';

/** Shape of a row sort configuration stored in indicator.formatting.row_sort */
export interface RowSortConfig {
  enabled?: boolean;
  key?: string;
  direction?: 'asc' | 'desc';
}

export const DEFAULT_ROW_SORT: Required<RowSortConfig> = {
  enabled: false,
  key: 'base_value',
  direction: 'desc',
};

export interface RowSortProperties {
  /** Current saved sort configuration for this indicator */
  rowSort: RowSortConfig | undefined;
  /**
   * Whether the indicator has an 'Expand by' dimension selected.
   * Sort only applies to expanded dimension rows so controls are
   * disabled when no dimension has been chosen.
   */
  hasDimension: boolean;
  /**
   * True when the indicator uses percentage formatting.
   * Variance-percentage sort options are disabled for percentage
   * indicators because the backend suppresses those columns for them.
   */
  isPercentageIndicator: boolean;
  /**
   * Called with the full resolved config only when the user clicks Apply.
   * A single callback fires one backend reload per save action.
   */
  onRowSortChange: (rowSort: Required<RowSortConfig>) => void;
}

const sortKeyLabels: Record<string, string> = {
  base_value: 'Base value',
  comparison_value: 'Comparison value',
  prior_year_value: 'Prior year value',
  variance_to_prior_year_value: 'Var vs PY',
  variance_to_prior_year_pct: 'Var % vs PY',
  variance_to_comparison_value: 'Var vs Comparison',
  variance_to_comparison_pct: 'Var % vs Comparison',
};

/**
 * Decomposes the current sort config into a label and a direction arrow
 * as separate values so the arrow glyph can be rendered in its own element
 * and nudged to correct for its Unicode descender space.
 */
const sortButtonParts = (
  config: Required<RowSortConfig>,
): { label: string; arrow: string | null } => {
  if (!config.enabled) return { label: 'Disabled', arrow: null };
  return {
    label: sortKeyLabels[config.key] ?? config.key,
    arrow: config.direction === 'asc' ? '↑' : '↓',
  };
};

/**
 * Per-indicator row sort configuration panel.
 *
 * Rendered inside the formatting section of the instrument edit modal when
 * a variance indicator row is selected. Uses a Popover with local state and
 * Apply/Cancel behaviour so that all sort settings can be adjusted before
 * triggering a single backend recalculation.
 */
const RowSort = ({
  rowSort,
  hasDimension,
  isPercentageIndicator,
  onRowSortChange,
}: RowSortProperties) => {
  const [isOpen, setIsOpen] = useState(false);

  // Merge incoming saved settings with defaults; used to initialise local state
  const resolveConfig = (config: RowSortConfig | undefined): Required<RowSortConfig> => ({
    ...DEFAULT_ROW_SORT,
    ...config,
  });

  const [localSettings, setLocalSettings] = useState<Required<RowSortConfig>>(
    resolveConfig(rowSort),
  );

  // Re-sync local state whenever the saved config changes (e.g. different indicator selected)
  useEffect(() => {
    setLocalSettings(resolveConfig(rowSort));
  }, [rowSort]);

  const handleChange = (field: keyof Required<RowSortConfig>, value: any) => {
    setLocalSettings((previous) => ({ ...previous, [field]: value }));
  };

  const handleApply = () => {
    onRowSortChange(localSettings);
    setIsOpen(false);
  };

  const handleCancel = () => {
    // Discard unsaved changes
    setLocalSettings(resolveConfig(rowSort));
    setIsOpen(false);
  };

  const savedConfig = resolveConfig(rowSort);
  const { label: buttonLabel, arrow: buttonArrow } = hasDimension
    ? sortButtonParts(savedConfig)
    : { label: 'No dimension', arrow: null };

  return (
    <Flex mb={2} justifyContent={'space-between'} alignItems={'center'}>
      <Text color={'black.800'} fontWeight={'600'}>
        Row sorting
      </Text>

      <Popover isOpen={isOpen} onClose={handleCancel} placement='left'>
        <PopoverTrigger>
          <Button
            size='xs'
            variant='outline'
            onClick={() => {
              if (!hasDimension) return;
              setIsOpen((previous) => !previous);
            }}
            isDisabled={!hasDimension}
            title={
              hasDimension
                ? 'Configure row sorting'
                : "Select an 'Expand by' dimension to enable row sorting"
            }
          >
            {buttonLabel}
            {buttonArrow && (
              /* Nudge the arrow glyph up to compensate for its Unicode descender space */
              <chakra.span position='relative' top='-1px' ml='1'>
                {buttonArrow}
              </chakra.span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent bg='white' p={2} borderRadius='sm' boxShadow='lg' width={'325px'}>
          <PopoverArrow />
          <PopoverHeader fontWeight='bold' color='#6562CF' fontSize='md'>
            Row sorting
          </PopoverHeader>
          <PopoverBody>
            <Stack spacing={3} p={1}>
              <Checkbox
                size='lg'
                isChecked={localSettings.enabled}
                onChange={(event: any) => handleChange('enabled', event.target.checked)}
                label={
                  <Text
                    variant='secondary'
                    fontFamily='regular_roboto'
                    fontSize='xs'
                    color={'black'}
                  >
                    Enable row sorting
                  </Text>
                }
              />

              {localSettings.enabled && (
                <>
                  <Box>
                    <Text color={'gray.600'} mb={1} fontSize='xs'>
                      Sort by
                    </Text>
                    <Select
                      size='sm'
                      value={localSettings.key}
                      onChange={(event: any) => handleChange('key', event.target.value)}
                    >
                      <option value='base_value'>Base scenario value</option>
                      <option value='comparison_value'>Comparison scenario value</option>
                      <option value='prior_year_value'>Prior year value</option>
                      <option value='variance_to_prior_year_value'>
                        Variance value vs Prior year
                      </option>
                      <option value='variance_to_prior_year_pct' disabled={isPercentageIndicator}>
                        Variance % vs Prior year
                        {isPercentageIndicator ? ' (n/a for % indicators)' : ''}
                      </option>
                      <option value='variance_to_comparison_value'>
                        Variance value vs Comparison
                      </option>
                      <option value='variance_to_comparison_pct' disabled={isPercentageIndicator}>
                        Variance % vs Comparison
                        {isPercentageIndicator ? ' (n/a for % indicators)' : ''}
                      </option>
                    </Select>
                    {isPercentageIndicator && (
                      <Text fontSize='xs' color='gray.500' mt={1}>
                        Variance % sorting is not available for percentage indicators.
                      </Text>
                    )}
                  </Box>

                  <Box>
                    <Text color={'gray.600'} mb={1} fontSize='xs'>
                      Direction
                    </Text>
                    <Select
                      size='sm'
                      value={localSettings.direction}
                      onChange={(event: any) => handleChange('direction', event.target.value)}
                    >
                      <option value='desc'>Descending</option>
                      <option value='asc'>Ascending</option>
                    </Select>
                  </Box>
                </>
              )}

              <Flex justifyContent='flex-end' gap={2} pt={1}>
                <Button variant={'cancel_button'} onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant={'save_button'} onClick={handleApply}>
                  Apply
                </Button>
              </Flex>
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Flex>
  );
};

export default RowSort;
